import React from "react"
import { useTranslation } from "react-i18next"

import * as TeamApi from "features/api/TeamApi"
import ProtectedLayout from "layouts/Protected"
import { privateRoutes } from "router/routes"

import { useAuth, useModal, useAlert, useDrawer } from "hooks"

import { NavLink, useLocation, useParams } from "react-router-dom"
import { TeamTable, FormProfil } from "features/Team/Partials"
import CaretIcon from "components/Elements/Icons/Caret"

export const TeamPartner = () => {
    const [team, setTeam] = React.useState(null)

    const { showModal } = useModal()
    const { showDrawer } = useDrawer()
    const { pathname, partnerProps } = useLocation()
    const { slug } = useParams()
    const { alert } = useAlert()
    const { t } = useTranslation()
    const { user, logout } = useAuth()

    const fetchData = React.useCallback(
        () => {
            ; (async () => {
                try {
                    const { res: getTeam, getError } = await TeamApi.getTeam(slug)
                    if (getError()) throw getError()
                    const newTeam = getTeam.filter(member => member?.status !== 9).map((member) => {
                        return ({
                            name: member?.description?.nom,
                            firstname: member?.description?.prenom,
                            mail: member?.mail,
                            role: member?.permission,
                            status: member?.status,
                            userid: member?.user_id,
                            rowversion: member?.row_version,
                            iseditable: (member.user_id === user?.user_id || user?.permission !== 1) && member?.permission !== -1
                        })
                    })
                    setTeam(newTeam)
                } catch (error) {
                    console.error(error)
                    const erroMsg = error?.message?.toString()
                    if (erroMsg) alert("error", erroMsg)
    
                    if (["Access token is required."].includes(error?.error?.message)) logout()
                }
            })()
        },
        [] // eslint-disable-line
    )

    // fetch data on first load
    React.useEffect(() => fetchData(), []) // eslint-disable-line

    const openFormProfil = (profil) => {
        showDrawer(
            DrawerAddProfil,
            {
                body: FormProfil,
                bodyProps: {
                    profil: profil,
                    user: user,
                    slug: slug
                },
                callback: () => {
                    // refresh data
                    fetchData()
                }
            },
            { style: { maxWidth: "800px" } }
        )
    }

    const deleteProfil = (profil) => {
        showModal(
            ModalConfirmation,
            {
                body: () => (
                    <>
                        <h2 className="c-h2 text-primary font-bold text-center my-7">{t("confirm_delete_title", { ns: "team" })}</h2>
                        <p className="mb-7">{t("confirm_delete_explain", { ns: "team" })}</p>
                        <p className="mb-7">{t("profil.mail", { ns: "team" })} : <strong>{profil.mail}</strong></p>
                    </>
                ),
                callback: () => {
                    ; (async () => {
                        try {
                            // API Call: delete user
                            const deleteProfil = {
                                user_id: profil.userid,
                                status: 9,
                                row_version: profil.rowversion,
                                // gender_id: getEntityId()
                                // permission: profil.role
                            }
                            
                            const { getError } = await TeamApi.putProfil(deleteProfil)
                            if (getError()) throw getError()
                            alert("success", t("delete_success", { ns: "team" }))
                            fetchData()
                        } catch (error) {
                            console.error(error)
                            const erroMsg = error?.message?.toString()
                            if (erroMsg) alert("error", erroMsg)
            
                            if (["Access token is required."].includes(error?.error?.message)) logout()
                        }
                    })()
                }
            },
            { style: { maxWidth: "800px" } }
        )
    }

    return (
        <ProtectedLayout className="px-5 py-7 lg:py-7">
            <div className="l-container">

                <div className="c-section-title">
                    <div className="c-section-title__content justify-between">
                        <div className="flex items-center justify-start w-full">
                            <NavLink to={privateRoutes.validatedPartner.path} className="flex">
                                <span className="flex py-2" style={{ transform: "rotate(180deg)" }}><CaretIcon size={30} /></span>
                            </NavLink>
                            <h1 className="c-section-title__title c-h2 text-primary font-medium ml-5">
                                {t("title_edit", { ns: "partner" }) } {partnerProps}
                            </h1>
                        </div>
                    </div>
                </div>

                {user.status === 1 &&
                    <div className="c-submenu p-7">
                        <div className="flex items-center justify-start w-full">
                            <div className={"c-submenu__item px-5 " + (pathname === `/partenaire/${slug}` ? "is-active" : "")}>
                                <NavLink to={`/partenaire/${slug}`} className="flex">
                                    {t("menu.store")}
                                </NavLink>
                            </div>
                            <div className={"c-submenu__item px-5 " + (pathname === `/gestion-equipe/${slug}` ? "is-active" : "")}>
                                <NavLink to={`/gestion-equipe-partenaire/${slug}`} className="flex">
                                    {t("menu.manage_team")}
                                </NavLink>
                            </div>
                        </div>
                    </div>
                }

                <div className="c-card p-5 lg:px-12 lg:py-7">
                    {team &&
                        <TeamTable
                            data={team}
                            openFormProfil={openFormProfil}
                            deleteProfil={deleteProfil}
                            className="pb-7"
                        />
                    }
                </div>

            </div>
        </ProtectedLayout>
    )
}

const DrawerAddProfil = ({ body, bodyProps, callback }) => {
    const { hideDrawer } = useDrawer()
    const validate = () => {
        hideDrawer()
        callback()
    }
    return (
        <div className="p-12">
            {React.createElement(
                body,
                {
                    ...bodyProps,
                    onFormProfilSuccess: validate
                }
            )}
        </div>
    )
}

const ModalConfirmation = ({ body, callback }) => {
    const { hideModal } = useModal()
    const { t } = useTranslation()
    const validate = () => {
        hideModal()
        callback()
    }
    return (
        <div className="p-12">
            {React.createElement(body)}
            <div className="flex justify-between flex-col md:flex-row-reverse">
                <button
                    type="button"
                    className="c-btn c-btn--secondary px-12 mb-5 lg:m-0"
                    onClick={validate}
                >
                    {t("global.confirm")}
                </button>
                <button
                    type="button"
                    className="c-btn c-btn--white-bordered px-7 mb-5 lg:m-0"
                    onClick={hideModal}
                >
                    {t("global.cancel")}
                </button>
            </div>
        </div>
    )
}
